import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.css";
import Header from "../../../components/Header";
import GameHistoryCard from "./GameHistoryCard";
import { requestApiPost } from "../../../api";
import { GET_TOURNAMENTS_HISTORY } from "../../../appConstants/APIEndpoint";
import { _isEmpty } from "../../../utils/helpers/zupeeLodash";
import { useTokenStore } from "../../../storage/tokenState";
import { useNavigate } from "react-router-dom";
import { getPartnerId } from "../../../utils/helpers/common";
import { FbPixelEvent_PageView } from "../../../utils/FbPixel/fbPixel";
import { FACEBOOK } from "../../../utils/helpers/constants";

// First, let's define an interface for the API response
interface GameHistoryItem {
  ltid: number;
  timestampForDateLabel: number;
  tournamentTitle: string;
  tournamentTimings: string;
  entriesText: string;
  amountWonText?: string;
  statusChips?: {
    label: string;
    backgroundColor: string;
  };
  isSuspended: boolean;
}

interface GameHistoryData {
  items: GameHistoryItem[];
  nextOffsetToken?: string;
}

const GameHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
    const { token: userToken } = useTokenStore();
    const { accessToken } = userToken;
  const [viewmoreCheck,setviewmoreCheck] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [tournamentHistory, setTournamentHistory] = useState<GameHistoryItem[]>([]);
  const offsetToken = useRef<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasTrackedRef = useRef(false);
  
  useEffect(() => {
    if (getPartnerId() === FACEBOOK && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      FbPixelEvent_PageView();
    }
  }, []);

  const fetchGameHistory = useCallback(async () => {
    if (isLoading || (offsetToken.current === '' && tournamentHistory.length > 0)) {
      return;
    }

    setIsLoading(true);
    const config = { headers: { Authorization: accessToken } };

    try {
      const res = await requestApiPost(
        GET_TOURNAMENTS_HISTORY,
        { gameName: 'LUDONX', offsetToken: offsetToken.current || '' },
        config
      );
      const response = res?.response;

      if (response?.success && response?.data) {
        const { items = [], nextOffsetToken } = response.data as GameHistoryData;

        // Update offsetToken ref - if nextOffsetToken is undefined, it will set empty string
        offsetToken.current = nextOffsetToken || '';

        // Update tournament history
        setTournamentHistory(prevHistory => {
          const newItems = items.filter(item => 
            !prevHistory.some(existingItem => existingItem.ltid === item.ltid)
          );
          return [...prevHistory, ...newItems];
        });

        // Hide view more if no nextOffsetToken is present
        if (!nextOffsetToken) {
          setviewmoreCheck(false);
        }
      }
    } catch (error) {
      console.error('Error fetching tournament history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const handleScroll = useCallback(() => {
    if (!containerRef?.current || !offsetToken.current || isLoading) {
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = containerRef.current;
    const bottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;

    if (bottom) {
      fetchGameHistory();
    }
  }, [fetchGameHistory, isLoading]);

  useEffect(() => {
    fetchGameHistory()
  }, [])

  useEffect(() => {
    const currentRef = containerRef.current;
    
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return(
    <>
      <div className={clsx(styles.pageWrapper, "pageWrapper")}>
        <Header pageTitle={t("gameHistoryLabel")} navigate={() => navigate(-1)} />
        <div  ref={containerRef}   className={clsx(styles.historyCardsContainer)}>
          {_isEmpty(tournamentHistory) ? (
            <div>No game history found</div>
          ) : (
            <React.Fragment>
              {tournamentHistory.map((item) => (
                <GameHistoryCard
                  key={item.ltid}
                  timestampForDateLabel={item.timestampForDateLabel}
                  tournamentTitle={item.tournamentTitle}
                  tournamentTimings={item.tournamentTimings}
                  entriesText={item.entriesText}
                  tag={item.statusChips?.label}
                  ltid={item?.ltid?.toString()}
                />
              ))}
            </React.Fragment>
          )}
          
          {offsetToken.current !== '' && viewmoreCheck && (
            <div 
              onClick={() => !isLoading && fetchGameHistory()} 
              className={clsx(
                styles.viewMoreBtn, 
                "fontColorSecondary fontSizeXs fontWeightBold",
                { [styles.disabled]: isLoading }
              )}
            >
              {isLoading ? t("loading") : t("viewMore")}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
export default GameHistoryPage;
